/**
 * Location Page — room type tabs, sort dropdown, filters modal, life-beyond carousel.
 * All data is server-rendered; JS shows/hides panels and reorders/filters cards.
 */
import Swiper from 'swiper';
import {Navigation} from 'swiper/modules';

export const LocationPage = {
    init() {
        // Search results page has its own minimal module — avoid the
        // location page's dropdown/sort/filter handlers fighting over the
        // same selectors there.
        if (document.body.classList.contains('page-search-results')) {
            this.initRoomCardToggle();
            return;
        }
        this.initRoomTabs();
        this.initSidebarDropdown();
        this.initOptionFilter();
        this.initDropdowns();
        this.initSort();
        this.initPriceChart();
        this.initFiltersModal();
        this.initLifeBeyondCarousel();
        this.initRoomCardToggle();
    },

    // ─── Filter cards in a panel by variant slug ───
    applyVariantFilter(panel, slug) {
        if (!panel) return;
        panel.querySelectorAll('.room-card').forEach((card) => {
            if (!slug) {
                card.style.display = '';
                return;
            }
            // Cards with no data-variant are not variant-specific — always show them.
            const cardVariant = card.dataset.variant;
            if (!cardVariant) {
                card.style.display = '';
                return;
            }
            card.style.display = (cardVariant === slug) ? '' : 'none';
        });
    },

    // ─── Room type tab switching ───
    initRoomTabs() {
        const sidebar = document.querySelector('.our-rooms__sidebar');
        const cardsWrap = document.querySelector('.our-rooms__cards');
        if (!sidebar || !cardsWrap) return;

        const tabs = sidebar.querySelectorAll('[data-room-tab]');
        const panels = cardsWrap.querySelectorAll('[data-room-panel]');

        const switchTab = (target) => {
            tabs.forEach((t) => {
                const isActive = t.dataset.roomTab === target;
                t.classList.toggle('is-active', isActive);
                t.setAttribute('aria-expanded', isActive ? 'true' : 'false');

                // Reset sub-filter on the activated tab: first option active, rest inactive.
                if (isActive) {
                    const options = t.querySelectorAll('.room-type-item');
                    options.forEach((opt, i) => {
                        opt.classList.toggle('is-active', i === 0);
                    });
                }
            });

            panels.forEach((p) => {
                p.classList.toggle('is-active', p.dataset.roomPanel === target);
            });

            // Apply filter based on the first active option of the new tab.
            const activeTab = sidebar.querySelector(`.room-tab[data-room-tab="${target}"]`);
            const firstOption = activeTab ? activeTab.querySelector('.room-type-item.is-active') : null;
            const activePanel = cardsWrap.querySelector(`.our-rooms__panel[data-room-panel="${target}"]`);
            const slug = firstOption ? firstOption.dataset.optionFilter : '';
            LocationPage.applyVariantFilter(activePanel, slug);

            // Re-apply modal filters (suburb + price) to the new tab.
            // Price sliders are reset to the new tab's full range (preserveState=false)
            // to prevent phantom price filtering caused by carrying over slider values
            // from a lower-priced tab. Area checkboxes still preserve their checked state.
            const modal = document.querySelector('[data-filters-modal]');
            if (modal) {
                LocationPage._syncFiltersToTab(modal, false);
                LocationPage._reapplyFilters(modal);
            }

            // Sidebar is sticky + max-height-capped, so a freshly-opened tab
            // can land partially below the visible area when the previous
            // tab was scrolled. Bring the active tab back into view inside
            // the sidebar's own scroll container so the user always sees
            // the section they just activated, plus the next tab below.
            if (activeTab) {
                const sidebarRect = sidebar.getBoundingClientRect();
                const tabRect = activeTab.getBoundingClientRect();
                const isAbove = tabRect.top < sidebarRect.top;
                const isBelow = tabRect.bottom > sidebarRect.bottom;
                if (isAbove || isBelow) {
                    const offset = activeTab.offsetTop - sidebar.offsetTop - 8;
                    sidebar.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' });
                }
            }
        };

        tabs.forEach((tab) => {
            // Attach click to the header only — sub-options (room-type-item) are outside the header
            // so they won't trigger tab switching.
            const header = tab.querySelector('.room-tab__header') || tab;
            header.addEventListener('click', (e) => {
                // Guard: ignore clicks originating inside sub-options.
                if (e.target.closest('.room-type-item')) return;
                switchTab(tab.dataset.roomTab);
            });
        });
    },

    // ─── Sidebar dropdown (replaces sidebar at laptop widths) ───
    initSidebarDropdown() {
        const dropdown = document.querySelector('[data-rooms-select]');
        const sidebar  = document.querySelector('.our-rooms__sidebar');
        if (!dropdown || !sidebar) return;

        const btn      = dropdown.querySelector('.our-rooms__sidebar-select-btn');
        const nameEl   = dropdown.querySelector('.our-rooms__sidebar-select-name');
        const priceEl  = dropdown.querySelector('.our-rooms__sidebar-select-btn > .our-rooms__sidebar-select-text > .our-rooms__sidebar-select-price');
        const textWrap = dropdown.querySelector('.our-rooms__sidebar-select-text');
        const options  = dropdown.querySelectorAll('.our-rooms__sidebar-select-item');
        if (!btn || !nameEl || !textWrap) return;

        const close = () => {
            dropdown.classList.remove('is-open');
            btn.setAttribute('aria-expanded', 'false');
        };
        const open = () => {
            dropdown.classList.add('is-open');
            btn.setAttribute('aria-expanded', 'true');
        };

        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            if (dropdown.classList.contains('is-open')) close();
            else open();
        });

        document.addEventListener('click', (e) => {
            if (!dropdown.contains(e.target)) close();
        });

        const updateTriggerLabel = (opt) => {
            nameEl.textContent = opt.dataset.optionName || '';
            const priceHtml = opt.dataset.optionPrice || '';
            let currentPrice = textWrap.querySelector('.our-rooms__sidebar-select-price');
            if (priceHtml) {
                if (!currentPrice) {
                    currentPrice = document.createElement('span');
                    currentPrice.className = 'our-rooms__sidebar-select-price';
                    textWrap.appendChild(currentPrice);
                }
                currentPrice.innerHTML = priceHtml;
            } else if (currentPrice) {
                currentPrice.remove();
            }
        };

        options.forEach((opt) => {
            opt.addEventListener('click', (e) => {
                e.stopPropagation();
                const tabSlug     = opt.dataset.roomsSelectTab;
                const variantSlug = opt.dataset.roomsSelectVariant;
                if (!tabSlug) return;

                // Switch room type tab by dispatching a click on the matching sidebar tab header.
                const tab = sidebar.querySelector(`.room-tab[data-room-tab="${tabSlug}"]`);
                const header = tab ? (tab.querySelector('.room-tab__header') || tab) : null;
                if (header) header.click();

                // Activate the specific variant sub-filter (skip if it's already the first/default).
                if (variantSlug && tab) {
                    const item = tab.querySelector(`.room-type-item[data-option-filter="${variantSlug}"]`);
                    if (item && !item.classList.contains('is-active')) {
                        item.click();
                    }
                }

                // Update dropdown UI state.
                updateTriggerLabel(opt);
                options.forEach((o) => {
                    const isActive = o === opt;
                    o.classList.toggle('is-active', isActive);
                    o.setAttribute('aria-selected', isActive ? 'true' : 'false');
                });
                close();
            });
        });
    },

    // ─── Room-type option sub-filter (e.g. 2-Bed, 3-Bed inside a tab) ───
    initOptionFilter() {
        const sidebar = document.querySelector('.our-rooms__sidebar');
        const cardsWrap = document.querySelector('.our-rooms__cards');
        if (!sidebar || !cardsWrap) return;

        // On page load, apply first variant filter for the active tab.
        const activeTab = sidebar.querySelector('.room-tab.is-active');
        if (activeTab) {
            const firstOpt = activeTab.querySelector('.room-type-item.is-active');
            const panel = cardsWrap.querySelector('.our-rooms__panel.is-active');
            if (firstOpt && panel) {
                LocationPage.applyVariantFilter(panel, firstOpt.dataset.optionFilter);
            }
        }

        sidebar.querySelectorAll('.room-type-item').forEach((item) => {
            item.addEventListener('click', (e) => {
                // Prevent bubbling to the parent .room-tab click handler.
                e.stopPropagation();

                // Toggle active state within the same tab's options group.
                const optionsWrap = item.closest('.room-tab__options');
                if (optionsWrap) {
                    optionsWrap.querySelectorAll('.room-type-item').forEach((i) => {
                        i.classList.toggle('is-active', i === item);
                    });
                }

                const activePanel = cardsWrap.querySelector('.our-rooms__panel.is-active');
                LocationPage.applyVariantFilter(activePanel, item.dataset.optionFilter);
            });
        });
    },

    // ─── Generic dropdown toggle (sort + price selects) ───
    initDropdowns() {
        const closePanel = (t, p) => {
            if (t) t.setAttribute('aria-expanded', 'false');
            if (p) p.classList.remove('is-open');
        };

        const openPanel = (t, p) => {
            if (t) t.setAttribute('aria-expanded', 'true');
            if (p) {
                p.removeAttribute('hidden');
                p.classList.add('is-open');
            }
        };

        document.querySelectorAll('.sort-dropdown, .price-select').forEach((dropdown) => {
            const trigger = dropdown.querySelector('button[aria-expanded]');
            const panel = dropdown.querySelector('.sort-dropdown__panel, .price-select__panel');
            if (!trigger || !panel) return;

            // Remove hidden attr so CSS transition works; start closed via CSS.
            panel.removeAttribute('hidden');

            trigger.addEventListener('click', (e) => {
                e.stopPropagation();
                const isOpen = trigger.getAttribute('aria-expanded') === 'true';

                // Close all other dropdowns first.
                document.querySelectorAll('.sort-dropdown, .price-select').forEach((d) => {
                    if (d === dropdown) return;
                    const t = d.querySelector('button[aria-expanded]');
                    const p = d.querySelector('.sort-dropdown__panel, .price-select__panel');
                    closePanel(t, p);
                });

                if (isOpen) {
                    closePanel(trigger, panel);
                } else {
                    openPanel(trigger, panel);
                }
            });

            document.addEventListener('click', (e) => {
                if (!dropdown.contains(e.target)) {
                    closePanel(trigger, panel);
                }
            });

            // Option selection.
            panel.querySelectorAll('button').forEach((option) => {
                option.addEventListener('click', () => {
                    const triggerText = trigger.querySelector('span');
                    if (triggerText) {
                        const prefix = dropdown.classList.contains('sort-dropdown') ? 'Sort: ' : '';
                        triggerText.textContent = prefix + option.textContent.trim();
                    }

                    panel.querySelectorAll('button').forEach((o) => o.classList.remove('is-active'));
                    option.classList.add('is-active');
                    closePanel(trigger, panel);
                });
            });
        });
    },

    // ─── Sort cards ───
    _applySortValue(value) {
        document.querySelectorAll('.our-rooms__panel').forEach((panel) => {
            const cards = Array.from(panel.querySelectorAll('.room-card'));

            cards.sort((a, b) => {
                if (value === 'price_asc') {
                    return (parseFloat(a.dataset.price) || 0) - (parseFloat(b.dataset.price) || 0);
                }
                if (value === 'price_desc') {
                    return (parseFloat(b.dataset.price) || 0) - (parseFloat(a.dataset.price) || 0);
                }
                // Tier-based sorts: matching tier comes first.
                const tierA = a.dataset.tier === value ? 0 : 1;
                const tierB = b.dataset.tier === value ? 0 : 1;
                return tierA - tierB;
            });

            cards.forEach((card) => panel.appendChild(card));
        });
    },

    initSort() {
        const sortDropdown = document.querySelector('[data-sort]');
        if (!sortDropdown) return;

        // Restore sort from URL.
        const urlSort = new URLSearchParams(window.location.search).get('sort');
        if (urlSort) {
            const option = sortDropdown.querySelector(`.sort-dropdown__option[data-value="${urlSort}"]`);
            if (option) {
                // Activate the option visually.
                sortDropdown.querySelectorAll('.sort-dropdown__option').forEach((o) => o.classList.remove('is-active'));
                option.classList.add('is-active');
                const triggerText = sortDropdown.querySelector('.sort-dropdown__trigger span');
                if (triggerText) {
                    triggerText.textContent = 'Sort: ' + option.textContent.trim();
                }
                this._applySortValue(urlSort);
            }
        }

        sortDropdown.addEventListener('click', (e) => {
            const option = e.target.closest('.sort-dropdown__option');
            if (!option) return;

            const value = option.dataset.value;
            this._applySortValue(value);

            // Save sort to URL.
            const params = new URLSearchParams(window.location.search);
            params.set('sort', value);
            history.replaceState(null, '', `${window.location.pathname}?${params}`);
        });
    },

    // ─── Price Chart + Range Slider ───
    initPriceChart() {
        const chart = document.querySelector('[data-price-chart]');
        if (!chart) return;
        // Search results page has its own independent filter setup.
        if (chart.closest('[data-search-filters]')) return;

        const rangeMin = chart.querySelector('[data-range="min"]');
        const rangeMax = chart.querySelector('[data-range="max"]');
        if (!rangeMin || !rangeMax) return;

        const step = parseFloat(rangeMin.step);

        const updateBars = () => {
            const lo = parseFloat(rangeMin.value);
            const hi = parseFloat(rangeMax.value);
            // Re-query bars each time — they get rebuilt when switching tabs.
            chart.querySelectorAll('.price-chart__bar').forEach((bar) => {
                const bucket = parseFloat(bar.dataset.bucket);
                bar.classList.toggle('is-outside', bucket < lo || bucket > hi);
            });
        };

        const syncSelect = (type, value) => {
            const select = document.querySelector(`[data-price-select="${type}"]`);
            if (!select) return;
            const triggerText = select.querySelector('.price-select__trigger span');
            if (triggerText) {
                triggerText.textContent = `R${Number(value).toLocaleString()}`;
            }
            select.querySelectorAll('.price-select__option').forEach((o) => {
                o.classList.toggle('is-active', parseFloat(o.dataset.value) === value);
            });
        };

        rangeMin.addEventListener('input', () => {
            if (parseFloat(rangeMin.value) > parseFloat(rangeMax.value) - step) {
                rangeMin.value = parseFloat(rangeMax.value) - step;
            }
            syncSelect('min', parseFloat(rangeMin.value));
            updateBars();
        });

        rangeMax.addEventListener('input', () => {
            if (parseFloat(rangeMax.value) < parseFloat(rangeMin.value) + step) {
                rangeMax.value = parseFloat(rangeMin.value) + step;
            }
            syncSelect('max', parseFloat(rangeMax.value));
            updateBars();
        });

        // Sync price-select dropdowns → range sliders.
        document.querySelectorAll('[data-price-select] .price-select__option').forEach((opt) => {
            opt.addEventListener('click', () => {
                const selectEl = opt.closest('[data-price-select]');
                const type = selectEl.dataset.priceSelect;
                const val = parseFloat(opt.dataset.value);

                if (type === 'min') {
                    rangeMin.value = val;
                } else {
                    rangeMax.value = val;
                }

                // Clamp.
                if (parseFloat(rangeMin.value) > parseFloat(rangeMax.value) - step) {
                    if (type === 'min') rangeMin.value = parseFloat(rangeMax.value) - step;
                    else rangeMax.value = parseFloat(rangeMin.value) + step;
                }

                syncSelect('min', parseFloat(rangeMin.value));
                syncSelect('max', parseFloat(rangeMax.value));
                updateBars();
            });
        });

        // Store reference for clear/reset.
        this._priceChart = {rangeMin, rangeMax, updateBars, syncSelect};
    },

    // ─── Get active room tab slug ───
    _getActiveTabSlug() {
        const activeTab = document.querySelector('.room-tab.is-active');
        return activeTab ? activeTab.dataset.roomTab : '';
    },

    // ─── Sync filter modal content with active tab ───
    _syncFiltersToTab(modal, preserveState = false) {
        const filterData = window.thriveFilterData;
        if (!filterData) return;

        const slug = this._getActiveTabSlug();
        const tabData = filterData[slug];
        if (!tabData) return;

        // Show/hide suburb checkboxes — keep checked state for relevant suburbs.
        const checkboxes = modal.querySelectorAll('.filters-checkbox');
        checkboxes.forEach((cb) => {
            const area = cb.dataset.filterArea;
            const relevant = tabData.suburbs.includes(area);
            cb.style.display = relevant ? '' : 'none';
            // Only uncheck hidden (irrelevant) ones.
            if (!relevant) cb.classList.remove('is-checked');
        });

        // Update price range sliders and selects.
        const pMin = tabData.priceMin;
        const pMax = tabData.priceMax;

        // Capture current user selection before updating range.
        let userMin = null;
        let userMax = null;
        if (preserveState && this._priceChart) {
            const { rangeMin, rangeMax } = this._priceChart;
            userMin = parseFloat(rangeMin.value);
            userMax = parseFloat(rangeMax.value);
        }
        const singlePriceEl = document.querySelector('[data-single-price]');
        const priceChartEl = document.querySelector('[data-price-chart]');
        const priceInputsEl = document.querySelector('.filters-modal__price-inputs');

        // Single price: hide chart/sliders, show static value.
        if (pMin === pMax) {
            if (singlePriceEl) {
                const valEl = singlePriceEl.querySelector('.filters-modal__single-price-value');
                if (valEl) valEl.textContent = `R${Number(pMin).toLocaleString()}`;
                singlePriceEl.style.display = '';
            }
            if (priceChartEl) priceChartEl.style.display = 'none';
            if (priceInputsEl) priceInputsEl.style.display = 'none';
        } else {
            // Multiple prices: show chart, hide single price.
            if (singlePriceEl) singlePriceEl.style.display = 'none';
            if (priceChartEl) priceChartEl.style.display = '';
            if (priceInputsEl) priceInputsEl.style.display = '';

            if (this._priceChart) {
                const {rangeMin, rangeMax, updateBars, syncSelect} = this._priceChart;

                rangeMin.min = pMin;
                rangeMin.max = pMax;
                rangeMax.min = pMin;
                rangeMax.max = pMax;

                if (preserveState && userMin !== null && userMax !== null) {
                    // Clamp user's selection to new tab's range.
                    rangeMin.value = Math.max(userMin, pMin);
                    rangeMax.value = Math.min(userMax, pMax);
                    // Ensure min < max.
                    const step = parseFloat(rangeMin.step) || 500;
                    if (parseFloat(rangeMin.value) > parseFloat(rangeMax.value) - step) {
                        rangeMin.value = pMin;
                        rangeMax.value = pMax;
                    }
                } else {
                    rangeMin.value = pMin;
                    rangeMax.value = pMax;
                }

                // Rebuild price-select options.
                const step = parseFloat(rangeMin.step) || 500;
                document.querySelectorAll('[data-price-select]').forEach((sel) => {
                    const panel = sel.querySelector('.price-select__panel');
                    if (!panel) return;
                    panel.innerHTML = '';
                    for (let pp = pMin; pp <= pMax; pp += step) {
                        const btn = document.createElement('button');
                        btn.className = 'price-select__option';
                        btn.dataset.value = pp;
                        btn.textContent = `R${pp.toLocaleString()}`;
                        btn.addEventListener('click', () => {
                            const type = sel.dataset.priceSelect;
                            if (type === 'min') {
                                rangeMin.value = pp;
                            } else {
                                rangeMax.value = pp;
                            }
                            if (parseFloat(rangeMin.value) > parseFloat(rangeMax.value) - step) {
                                if (type === 'min') rangeMin.value = parseFloat(rangeMax.value) - step;
                                else rangeMax.value = parseFloat(rangeMin.value) + step;
                            }
                            syncSelect('min', parseFloat(rangeMin.value));
                            syncSelect('max', parseFloat(rangeMax.value));
                            updateBars();

                            // Close the dropdown panel.
                            const trigger = sel.querySelector('button[aria-expanded]');
                            const pnl = sel.querySelector('.price-select__panel');
                            if (trigger) trigger.setAttribute('aria-expanded', 'false');
                            if (pnl) pnl.classList.remove('is-open');
                        });
                        panel.appendChild(btn);
                    }
                });

                // Update selects AFTER rebuilding options so syncSelect
                // can match the freshly created option buttons.
                syncSelect('min', parseFloat(rangeMin.value));
                syncSelect('max', parseFloat(rangeMax.value));

                // Rebuild bars.
                const barsWrap = document.querySelector('.price-chart__bars');
                if (barsWrap) {
                    barsWrap.innerHTML = '';
                    for (let pv = pMin; pv <= pMax; pv += step) {
                        const h = Math.max(Math.round((pv / pMax) * 68), 4);
                        const bar = document.createElement('div');
                        bar.className = 'price-chart__bar';
                        bar.dataset.bucket = pv;
                        bar.style.height = `${h}px`;
                        barsWrap.appendChild(bar);
                    }
                }

                updateBars();
            }
        }
    },

    // ─── Count how many cards match current filter state (active panel only) ───
    _countFilteredCards(modal) {
        const checkboxes = modal.querySelectorAll('.filters-checkbox');

        // Collect checked areas (only visible ones).
        const activeAreas = [];
        checkboxes.forEach((cb) => {
            if (cb.classList.contains('is-checked') && cb.style.display !== 'none') {
                activeAreas.push(cb.dataset.filterArea);
            }
        });

        // Collect price range.
        let minPrice = 0;
        let maxPrice = Infinity;
        if (this._priceChart) {
            const {rangeMin, rangeMax} = this._priceChart;
            minPrice = parseFloat(rangeMin.value);
            maxPrice = parseFloat(rangeMax.value);
            if (minPrice <= parseFloat(rangeMin.min) && maxPrice >= parseFloat(rangeMax.max)) {
                minPrice = 0;
                maxPrice = Infinity;
            }
        }

        // Count matching cards in active panel only.
        const activePanel = document.querySelector('.our-rooms__panel.is-active');
        if (!activePanel) return 0;

        let count = 0;
        activePanel.querySelectorAll('.room-card').forEach((card) => {
            const cardSuburb = card.dataset.suburb;
            const cardPrice = parseFloat(card.dataset.price) || 0;
            const areaMatch = activeAreas.length === 0 || activeAreas.includes(cardSuburb);
            const priceMatch = cardPrice >= minPrice && cardPrice <= maxPrice;
            if (areaMatch && priceMatch) count++;
        });
        return count;
    },

    // ─── Update apply button with live count ───
    _updateApplyCount(modal) {
        const applyBtn = modal.querySelector('[data-filters-apply]');
        if (!applyBtn) return;
        const count = this._countFilteredCards(modal);
        applyBtn.textContent = 'Show results';
    },

    // ─── Re-apply current filter state to visible cards ───
    _reapplyFilters(modal) {
        const checkboxes = modal.querySelectorAll('.filters-checkbox');
        const activeAreas = [];
        checkboxes.forEach((cb) => {
            if (cb.classList.contains('is-checked') && cb.style.display !== 'none') {
                activeAreas.push(cb.dataset.filterArea);
            }
        });

        let minPrice = 0;
        let maxPrice = Infinity;
        if (this._priceChart) {
            const { rangeMin, rangeMax } = this._priceChart;
            minPrice = parseFloat(rangeMin.value);
            maxPrice = parseFloat(rangeMax.value);
            if (minPrice <= parseFloat(rangeMin.min) && maxPrice >= parseFloat(rangeMax.max)) {
                minPrice = 0;
                maxPrice = Infinity;
            }
        }

        // Only apply if user has active filters.
        const hasFilters = activeAreas.length > 0 || minPrice > 0 || maxPrice < Infinity;
        if (!hasFilters) return;

        document.querySelectorAll('.our-rooms__panel').forEach((panel) => {
            panel.querySelectorAll('.room-card').forEach((card) => {
                const cardSuburb = card.dataset.suburb;
                const cardPrice = parseFloat(card.dataset.price) || 0;
                const areaMatch = activeAreas.length === 0 || activeAreas.includes(cardSuburb);
                const priceMatch = cardPrice >= minPrice && cardPrice <= maxPrice;
                card.style.display = (areaMatch && priceMatch) ? '' : 'none';
            });
        });
    },

    // ─── URL filter persistence ───
    _saveFiltersToURL(areas, minPrice, maxPrice) {
        const params = new URLSearchParams(window.location.search);

        if (areas.length) params.set('areas', areas.join(','));
        else params.delete('areas');

        // Only persist price if user changed it from the range defaults.
        const hasCustomPrice = this._priceChart && (
            minPrice > parseFloat(this._priceChart.rangeMin.min) ||
            maxPrice < parseFloat(this._priceChart.rangeMax.max)
        );
        if (hasCustomPrice && minPrice > parseFloat(this._priceChart.rangeMin.min)) {
            params.set('min_price', minPrice);
        } else {
            params.delete('min_price');
        }
        if (hasCustomPrice && maxPrice < parseFloat(this._priceChart.rangeMax.max)) {
            params.set('max_price', maxPrice);
        } else {
            params.delete('max_price');
        }

        const qs = params.toString();
        history.replaceState(null, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
    },

    _restoreFiltersFromURL(modal) {
        const params = new URLSearchParams(window.location.search);
        const areasStr = params.get('areas');
        const minStr = params.get('min_price');
        const maxStr = params.get('max_price');
        if (!areasStr && !minStr && !maxStr) return;

        // Restore area checkboxes.
        if (areasStr) {
            const areas = areasStr.split(',');
            modal.querySelectorAll('.filters-checkbox').forEach((cb) => {
                if (areas.includes(cb.dataset.filterArea)) {
                    cb.classList.add('is-checked');
                }
            });
        }

        // Restore price sliders.
        if (this._priceChart && (minStr || maxStr)) {
            const {rangeMin, rangeMax, updateBars, syncSelect} = this._priceChart;
            if (minStr) {
                rangeMin.value = parseFloat(minStr);
                syncSelect('min', parseFloat(minStr));
            }
            if (maxStr) {
                rangeMax.value = parseFloat(maxStr);
                syncSelect('max', parseFloat(maxStr));
            }
            updateBars();
        }

        // Apply the restored filters to cards.
        this._reapplyFilters(modal);
    },

    // ─── Filters Modal ───
    initFiltersModal() {
        const modal = document.querySelector('[data-filters-modal]');
        if (!modal) return;
        // Search results page has its own handler — skip here.
        if (modal.hasAttribute('data-search-filters')) return;

        const openBtns = document.querySelectorAll('[data-filters-open]');
        const closeBtns = modal.querySelectorAll('[data-filters-close]');
        const clearBtn = modal.querySelector('[data-filters-clear]');
        const applyBtn = modal.querySelector('[data-filters-apply]');
        const checkboxes = modal.querySelectorAll('.filters-checkbox');

        // Live count updater.
        const updateCount = () => this._updateApplyCount(modal);

        // Restore filters from URL on page load.
        this._restoreFiltersFromURL(modal);

        // Snapshot of filter state before opening — restored on close without Apply.
        let snapshot = null;

        const takeSnapshot = () => {
            const checkedAreas = [];
            checkboxes.forEach((cb) => {
                if (cb.classList.contains('is-checked')) checkedAreas.push(cb.dataset.filterArea);
            });
            const s = {areas: checkedAreas};
            if (this._priceChart) {
                s.min = this._priceChart.rangeMin.value;
                s.max = this._priceChart.rangeMax.value;
            }
            return s;
        };

        const restoreSnapshot = (s) => {
            if (!s) return;
            checkboxes.forEach((cb) => {
                cb.classList.toggle('is-checked', s.areas.includes(cb.dataset.filterArea));
            });
            if (this._priceChart && s.min != null) {
                const {rangeMin, rangeMax, updateBars, syncSelect} = this._priceChart;
                rangeMin.value = s.min;
                rangeMax.value = s.max;
                syncSelect('min', parseFloat(s.min));
                syncSelect('max', parseFloat(s.max));
                updateBars();
            }
        };

        // Open — sync filters to active tab, preserving current slider state.
        openBtns.forEach((btn) => {
            btn.addEventListener('click', () => {
                this._syncFiltersToTab(modal, true);
                snapshot = takeSnapshot();
                modal.classList.add('is-open');
                modal.setAttribute('aria-hidden', 'false');
                document.body.style.overflow = 'hidden';
                document.body.classList.add('filters-open');
                updateCount();
            });
        });

        // Close without Apply — restore previous state.
        const closeModal = (applied) => {
            modal.classList.remove('is-open');
            modal.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
            document.body.classList.remove('filters-open');
            if (!applied) restoreSnapshot(snapshot);
        };
        closeBtns.forEach((btn) => btn.addEventListener('click', () => closeModal(false)));

        // Escape key.
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modal.classList.contains('is-open')) {
                closeModal(false);
            }
        });

        // Checkbox toggle — update count live.
        checkboxes.forEach((cb) => {
            cb.addEventListener('click', () => {
                cb.classList.toggle('is-checked');
                updateCount();
            });
        });

        // Price slider changes — update count live.
        if (this._priceChart) {
            const {rangeMin, rangeMax} = this._priceChart;
            rangeMin.addEventListener('input', updateCount);
            rangeMax.addEventListener('input', updateCount);
        }

        // Clear filters.
        if (clearBtn) {
            clearBtn.addEventListener('click', () => {
                checkboxes.forEach((cb) => cb.classList.remove('is-checked'));

                // Reset price selects to actual min/max values.
                modal.querySelectorAll('.price-select__trigger span').forEach((s) => {
                    const sel = s.closest('[data-price-select]');
                    if (sel && this._priceChart) {
                        const { rangeMin: rMin, rangeMax: rMax } = this._priceChart;
                        const val = sel.dataset.priceSelect === 'min' ? rMin.min : rMax.max;
                        s.textContent = `R${Number(val).toLocaleString()}`;
                    }
                });
                modal.querySelectorAll('.price-select__option').forEach((o) => o.classList.remove('is-active'));

                // Reset price chart sliders.
                if (this._priceChart) {
                    const {rangeMin, rangeMax, updateBars} = this._priceChart;
                    rangeMin.value = rangeMin.min;
                    rangeMax.value = rangeMax.max;
                    updateBars();
                }

                // Clear URL filter params.
                this._saveFiltersToURL([], 0, Infinity);

                updateCount();
            });
        }

        // Apply filters.
        if (applyBtn) {
            applyBtn.addEventListener('click', () => {
                // Collect checked areas (only visible ones).
                const activeAreas = [];
                checkboxes.forEach((cb) => {
                    if (cb.classList.contains('is-checked') && cb.style.display !== 'none') {
                        activeAreas.push(cb.dataset.filterArea);
                    }
                });

                // Collect price range from sliders.
                let minPrice = 0;
                let maxPrice = Infinity;
                if (this._priceChart) {
                    const {rangeMin, rangeMax} = this._priceChart;
                    minPrice = parseFloat(rangeMin.value);
                    maxPrice = parseFloat(rangeMax.value);
                    if (minPrice <= parseFloat(rangeMin.min) && maxPrice >= parseFloat(rangeMax.max)) {
                        minPrice = 0;
                        maxPrice = Infinity;
                    }
                }

                // Apply to all panels (so switching tabs keeps filters).
                document.querySelectorAll('.our-rooms__panel').forEach((panel) => {
                    panel.querySelectorAll('.room-card').forEach((card) => {
                        const cardSuburb = card.dataset.suburb;
                        const cardPrice = parseFloat(card.dataset.price) || 0;
                        const areaMatch = activeAreas.length === 0 || activeAreas.includes(cardSuburb);
                        const priceMatch = cardPrice >= minPrice && cardPrice <= maxPrice;
                        card.style.display = (areaMatch && priceMatch) ? '' : 'none';
                    });
                });

                // Persist filters to URL.
                this._saveFiltersToURL(activeAreas, minPrice, maxPrice);

                closeModal(true);
            });
        }
    },

    // ─── Mobile room card expand/collapse toggle ───
    initRoomCardToggle() {
        document.querySelectorAll('.room-card__toggle').forEach((btn) => {
            btn.addEventListener('click', () => {
                const card = btn.closest('.room-card');
                card.classList.toggle('is-expanded');
            });
        });
    },

    // ─── Life Beyond Campus Swiper ───
    initLifeBeyondCarousel() {
        document.querySelectorAll('[data-life-beyond-swiper]').forEach((wrapper) => {
            const swiperEl = wrapper.querySelector('.swiper');
            if (!swiperEl) return;

            const prevBtn = wrapper.querySelector('[data-life-prev]');
            const nextBtn = wrapper.querySelector('[data-life-next]');
            const currentEl = wrapper.querySelector('.life-beyond__counter-current');
            const totalEl = wrapper.querySelector('.life-beyond__counter-total');

            const swiper = new Swiper(swiperEl, {
                modules: [Navigation],
                slidesPerView: 'auto',
                spaceBetween: 32,
                navigation: {
                    prevEl: prevBtn,
                    nextEl: nextBtn,
                },
            });

            if (currentEl || totalEl) {
                const updateCounter = () => {
                    if (currentEl) {
                        currentEl.textContent = String(swiper.snapIndex + 1).padStart(2, '0');
                    }
                    if (totalEl) {
                        totalEl.textContent = String(Math.max(swiper.snapGrid.length, 1)).padStart(2, '0');
                    }
                };
                updateCounter();
                swiper.on('slideChange', updateCounter);
                swiper.on('resize', updateCounter);
            }
        });
    },
};
